import Circle from 'ol/style/Circle'
import Fill from 'ol/style/Fill'
import Icon from 'ol/style/Icon'
import Stroke from 'ol/style/Stroke'
import Style from 'ol/style/Style'
import Text from 'ol/style/Text'

// Global style pool - eliminates clone() calls by pre-allocating all possible styles
const stylePool = new Map()

// Text style cache for labels (keyed by label text)
const textStyleCache = new Map()

const styleCache = {
  default: null,
  default_selected: null,
  ad_link: null,
  ad_link_selected: null,
  not_vabaturu_tehing: null,
  not_vabaturu_tehing_selected: null,
  marked_for_comparison: null,
  marked_for_comparison_selected: null,
  cluster: {},
  cluster_selected: {},
  cluster_ad_link: {},
  cluster_marked_for_comparison: {},
}

function getDefaultStyle() {
  const style = {
    fill: new Fill({
      color: 'rgba(255,255,255,0.4)',
    }),
    stroke: new Stroke({
      color: '#3399CC',
      width: 2,
    }),
  }
  style.image = new Circle({
    fill: style.fill,
    stroke: style.stroke,
    radius: 5,
  })
  return style
}

function getTransactionStyle() {
  const conf = {
    default: {
      color: '#027BFA',
    },
  }

  const color = conf.default?.color || '#027BFA'
  const svgMarker = createMarkerSvg(color)
  const encodedSvg = encodeURIComponent(svgMarker)
  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodedSvg}`

  const style = {
    image: new Icon({
      src: dataUrl,
      scale: 1,
      anchor: [0.5, 1],
      crossOrigin: 'anonymous',
    }),
  }

  return style
}

function buildStyle(style) {
  let olStyle = {}

  if (style) {
    Object.keys(style).forEach(key => {
      switch (key) {
        case 'stroke':
          olStyle[key] = new Stroke(style[key])
          break
        case 'fill':
          olStyle[key] = new Fill(style[key])
          break
        case 'image':
        case 'circle':
          olStyle['image'] = new Circle({
            fill: style[key].fill && new Fill(style[key].fill),
            stroke: style[key].stroke && new Stroke(style[key].stroke),
            radius: style[key].radius || 5,
            // Optional pixel-space offset so callers can anchor the
            // circle to a corner of the underlying marker (RED-180
            // broker overlay uses this for the orange selection ring).
            ...(style[key].displacement ? { displacement: style[key].displacement } : {}),
          })
          break
        case 'shape':
          if (style[key].fill) {
            style[key].fill = new Fill(style[key].fill)
          }
          if (style[key].stroke) {
            style[key].stroke = new Stroke(style[key].stroke)
          }
          olStyle['image'] = new RegularShape(style[key])
          break
        case 'icon':
          olStyle['image'] = new Icon(style[key])
          break
        case 'text':
          if (style[key].fill) {
            style[key].fill = new Fill(style[key].fill)
          }
          if (style[key].stroke) {
            style[key].stroke = new Stroke(style[key].stroke)
          }
          olStyle[key] = new Text(style[key])
          break
        // zIndex, geometry
        case 'transaction':
          olStyle = getTransactionStyle()
          break
        default:
          olStyle[key] = style[key]
      }
    })
  } else {
    olStyle = getDefaultStyle()
  }
  return new Style(olStyle)
}

// Default ring configuration for "open" state
const defaultRingConfig = {
  color: '#6366f1', // Indigo
  width: 2.5,
  scale: 1.1,
}

// Corner "seen" check badge for the RED-236 viewed cue. A slate disc with a
// white tick, anchored to the top-right of the marker body. Kept inside the
// 28×32 viewBox so it works identically in the WebGL and Canvas render paths.
// The marker keeps its own hue at full strength — the badge is the only added
// element (no desaturation, no indigo — indigo is reserved for search). This is
// the confirmed RED-236 viewed cue (Toomas), chosen over RED-255's desaturation.
const VIEWED_BADGE = `<circle cx="22.5" cy="5.5" r="5.5" fill="#475569" stroke="#ffffff" stroke-width="1.5"/><path d="M19.6 5.6 L21.5 7.5 L25.3 3.5" fill="none" stroke="#ffffff" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>`

// White glyphs drawn on the colored marker body (transparent background).
// Same visual language as the house: solid white silhouette, no strokes.
const MARKER_GLYPHS = {
  // House with a door
  house: '<path fill="white" d="M14 6L9 10V18H19V10L14 6ZM12 12H14V18H12V12Z" />',
  // Maatükk (RED-255): two staggered spruces — unmistakably land at any size
  land: `
            <path fill="white" d="M10.5 5.5 L14.6 11 L12.9 11 L15.3 15.3 L11.6 15.3 L11.6 18.3 L9.4 18.3 L9.4 15.3 L5.7 15.3 L8.1 11 L6.4 11 Z" />
            <path fill="white" d="M18.3 9.2 L21.6 15.9 L19.2 15.9 L19.2 18.5 L17.4 18.5 L17.4 15.9 L15 15.9 Z" />`,
}

// Create icon SVGs for different markers with proper cache. `glyph` picks the
// body silhouette (house | land, RED-255); `viewed` adds the RED-236 corner
// check badge (full colour, no desaturation).
const createMarkerSvg = (color, ringConfig = null, glyph = 'house', viewed = false) => {
  // Ring stroke around the marker body for "open" state
  const ringStroke = ringConfig
    ? `<rect x="0" y="0" width="28" height="24" rx="4" ry="4" fill="none" stroke="${ringConfig.color}" stroke-width="${ringConfig.width}"/>`
    : ''

  return `
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="32" viewBox="0 0 28 32">
            ${ringStroke}
            <rect x="2" y="2" width="24" height="20" rx="3" ry="3" fill="${color}" />
            ${MARKER_GLYPHS[glyph] || MARKER_GLYPHS.house}
            <path fill="${color}" d="M14 28L10 22L18 22L14 28" />
            ${viewed ? VIEWED_BADGE : ''}
        </svg>
    `
}

// Create cluster SVGs for different types
const createClusterSvg = (color, ringConfig = null) => {
  // Ring stroke around the cluster body for "open" state
  const ringStroke = ringConfig
    ? `<rect x="0" y="0" width="28" height="24" rx="4" ry="4" fill="none" stroke="${ringConfig.color}" stroke-width="${ringConfig.width}"/>`
    : ''

  return `
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="32" viewBox="0 0 28 32">
            ${ringStroke}
            <rect x="2" y="2" width="24" height="20" rx="3" ry="3" fill="${color}" />
            <path fill="${color}" d="M14 28L10 22L18 22L14 28" />
        </svg>
    `
}

export const markerStyle = (key, conf, selected = false) => {
  // Use the color from configuration or fallback to default
  const color = conf[key]?.color || conf.default?.color || '#3399CC'

  // Check if we already have this style cached
  const cacheKey = selected ? key + '_selected' : key
  if (styleCache[cacheKey]) {
    return styleCache[cacheKey]
  }

  // Create SVG marker
  const svgMarker = createMarkerSvg(color)

  // Convert SVG to data URL
  const encodedSvg = encodeURIComponent(svgMarker)
  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodedSvg}`

  // Create style
  const style = new Style({
    image: new Icon({
      src: dataUrl,
      scale: selected ? 1.2 : 1,
      anchor: [0.5, 1],
      crossOrigin: 'anonymous',
    }),
    // Use minimal stroke for better performance
    stroke: new Stroke({
      color: color,
      width: 1,
    }),
  })

  // Cache the style
  styleCache[cacheKey] = style

  return style
}

export const createStyle = conf => {
  if (Array.isArray(conf)) {
    return conf.map(style => buildStyle(style))
  } else {
    return buildStyle(conf)
  }
}

export const createTransactionsStyle = conf => {
  // Get ring configuration from conf or use defaults
  const ringConfig = {
    ...defaultRingConfig,
    ...conf.openRing,
  }

  // Initialize style pool once with all possible combinations
  // This eliminates the need for clone() calls entirely
  const initStylePool = () => {
    if (stylePool.size > 0) return // Already initialized

    const iconTypes = ['default', 'ad_link', 'ad_link_not_found', 'not_vabaturu_tehing', 'marked_for_comparison']
    const states = ['', '_selected', '_open', '_open_selected']

    // Pre-create all marker style combinations. Non-open states additionally get
    // {house, land glyph} x {fresh, viewed} variants (RED-255); an "open" marker is
    // actively inspected, so viewed-dimming doesn't apply there.
    iconTypes.forEach(iconType => {
      states.forEach(state => {
        const isOpen = state.includes('_open')
        const isSelected = state.includes('_selected')

        if (isOpen) {
          const style = createMarkerStyleOpen(iconType, conf, ringConfig)
          if (isSelected) {
            style.getImage().setScale(1.25) // Slightly larger for open+selected
          }
          stylePool.set(`marker_${iconType}${state}`, style)
        } else {
          ;[false, true].forEach(land => {
            ;[false, true].forEach(viewed => {
              const key = `marker_${iconType}${land ? '_land' : ''}${viewed ? '_viewed' : ''}${state}`
              const style = createMarkerStyle(iconType, conf, land ? 'land' : 'house', viewed)
              if (isSelected) {
                style.getImage().setScale(1.2)
              }
              stylePool.set(key, style)
            })
          })
        }
      })
    })

    // Pre-create cluster styles for different types and states
    const clusterTypes = {
      cluster: conf.cluster?.color || conf.default?.color || '#027BFA',
      cluster_ad_link: conf.ad_link?.color || '#2DA74E',
      cluster_ad_link_not_found: conf.ad_link_not_found?.color || '#FFC107',
      cluster_not_vabaturu_tehing: conf.not_vabaturu_tehing?.color || '#FF4081',
      cluster_marked_for_comparison: conf.marked_for_comparison?.color || '#DB3848',
    }

    Object.entries(clusterTypes).forEach(([clusterType, color]) => {
      states.forEach(state => {
        const key = `${clusterType}${state}`
        const isOpen = state.includes('_open')
        const isSelected = state.includes('_selected')

        if (isOpen) {
          const style = createClusterStyleOpen(color, ringConfig)
          if (isSelected) {
            style.getImage().setScale(1.25)
            style.getText()?.setScale?.(1.25)
          }
          stylePool.set(key, style)
        } else {
          const style = createClusterStyle(color)
          if (isSelected) {
            style.getImage().setScale(1.2)
            style.getText()?.setScale?.(1.2)
          }
          stylePool.set(key, style)
        }
      })
    })
  }

  // Get or create cached text style
  const getTextStyle = (text, offsetY) => {
    const cacheKey = `${text}_${offsetY}`
    if (textStyleCache.has(cacheKey)) {
      return textStyleCache.get(cacheKey)
    }

    const textStyle = new Style({
      text: new Text({
        text: typeof text === 'number' ? text.toLocaleString('et-EE') : String(text),
        font: '12px sans-serif',
        backgroundFill: new Fill({
          color: 'rgba(255, 255, 255, 0.7)',
        }),
        backgroundStroke: new Stroke({
          color: '#6c757d',
        }),
        padding: [1, 1, 1, 2],
        offsetY: offsetY,
      }),
    })

    // Limit cache size to prevent memory leaks
    if (textStyleCache.size > 1000) {
      const firstKey = textStyleCache.keys().next().value
      textStyleCache.delete(firstKey)
    }

    textStyleCache.set(cacheKey, textStyle)
    return textStyle
  }

  // Initialize pool on first call
  initStylePool()

  // Return the style function - NO clone() calls, just pool lookups
  // Note: Properties are transformed from _icon/_open/_selected/_label to icon/isOpen/isSelected/label
  // to avoid WebGL GLSL reserved identifier issues
  return (feature, resolution) => {
    if (!feature) return null

    // Get properties - handle both OL features and GeoJSON features
    const props = feature.getProperties
      ? feature.getProperties()
      : feature.properties
    if (!props) return null

    // Build state suffix for pool lookup
    // Support both old (_open/_selected) and new (isOpen/isSelected) property names
    const isOpen = props.isOpen || props._open
    const isSelected = props.isSelected || props._selected
    let stateSuffix = ''
    if (isOpen && isSelected) {
      stateSuffix = '_open_selected'
    } else if (isOpen) {
      stateSuffix = '_open'
    } else if (isSelected) {
      stateSuffix = '_selected'
    }

    // Handle clusters
    if (props.cluster) {
      // Determine cluster style type
      let styleKey = 'cluster'
      const icon = props.icon || props._icon || ''
      if (icon.includes('marked_for_comparison')) {
        styleKey = 'cluster_marked_for_comparison'
      } else if (icon.includes('not_vabaturu_tehing')) {
        styleKey = 'cluster_not_vabaturu_tehing'
      } else if (icon === 'cluster_ad_link_not_found' || icon === 'ad_link_not_found') {
        styleKey = 'cluster_ad_link_not_found'
      } else if (icon.includes('ad_link')) {
        styleKey = 'cluster_ad_link'
      }

      // Get pooled style - NO clone() needed
      const poolKey = `${styleKey}${stateSuffix}`
      const clusterStyle = stylePool.get(poolKey) || stylePool.get('cluster')

      // For clusters, we need to update the text value
      // But instead of cloning, we can set it directly (style is reused per frame)
      const textObj = clusterStyle.getText()
      if (textObj && props.point_count) {
        // Same cap as the WebGL-mode label layer (createLabelOnlyStyle)
        textObj.setText(props.point_count > 99 ? '99+' : String(props.point_count))
      }

      return clusterStyle
    }

    // Handle single points
    else {
      // Determine icon type with fallback to default (support both property names)
      const iconType = props.icon || props._icon || 'default'
      const variantSuffix = stateSuffix.includes('_open')
        ? ''
        : `${props.land ? '_land' : ''}${props.viewed ? '_viewed' : ''}`

      // Pooled style — NO clone() needed. variantSuffix carries land (RED-255)
      // + viewed (RED-236 badge); an open marker keeps the ring treatment.
      const poolKey = `marker_${iconType}${variantSuffix}${stateSuffix}`
      const markerStyle = stylePool.get(poolKey) || stylePool.get('marker_default')

      // If we have a label, return array with cached text style (support both property names)
      const label = props.label || props._label
      if (label) {
        const offsetY = isOpen ? -38 : (isSelected ? -43 : -34)
        const textStyle = getTextStyle(label, offsetY)
        return [markerStyle, textStyle]
      }

      return markerStyle
    }
  }
}

// Helper functions
function createMarkerStyle(type, conf, glyph = 'house', viewed = false) {
  // RED-236: viewed markers keep full colour + gain the corner badge (no
  // desaturation, no opacity dimming) — the badge alone is the cue.
  const color = conf[type]?.color || conf.default?.color || '#027BFA'
  const svgMarker = createMarkerSvg(color, null, glyph, viewed)
  const encodedSvg = encodeURIComponent(svgMarker)
  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodedSvg}`

  return new Style({
    image: new Icon({
      src: dataUrl,
      scale: 1,
      anchor: [0.5, 1],
      crossOrigin: 'anonymous',
    }),
  })
}

function createClusterStyle(color) {
  const svgCluster = createClusterSvg(color)
  const encodedSvg = encodeURIComponent(svgCluster)
  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodedSvg}`

  return new Style({
    image: new Icon({
      src: dataUrl,
      scale: 1,
      // Pin tip on the point — matches the WebGL style array and the hover
      // highlight clone, so canvas mode renders/covers identically and
      // findMarkerAtPixel's icon boxes line up.
      anchor: [0.5, 1],
      crossOrigin: 'anonymous',
    }),
    text: new Text({
      text: '', // Will be set dynamically
      fill: new Fill({
        color: '#fff',
      }),
      // Body center of the 32px-tall pin (tip-anchored)
      offsetY: -20,
    }),
    font: '13px sans-serif',
  })
}

// Create marker style with ring for "open" state
function createMarkerStyleOpen(type, conf, ringConfig) {
  const color = conf[type]?.color || conf.default?.color || '#027BFA'
  const svgMarker = createMarkerSvg(color, ringConfig)
  const encodedSvg = encodeURIComponent(svgMarker)
  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodedSvg}`

  return new Style({
    image: new Icon({
      src: dataUrl,
      scale: ringConfig.scale,
      anchor: [0.5, 1],
      crossOrigin: 'anonymous',
    }),
  })
}

// Create cluster style with ring for "open" state
function createClusterStyleOpen(color, ringConfig) {
  const svgCluster = createClusterSvg(color, ringConfig)
  const encodedSvg = encodeURIComponent(svgCluster)
  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodedSvg}`

  return new Style({
    image: new Icon({
      src: dataUrl,
      scale: ringConfig.scale,
      // Tip-anchored like createClusterStyle — see comment there
      anchor: [0.5, 1],
      crossOrigin: 'anonymous',
    }),
    text: new Text({
      text: '',
      fill: new Fill({
        color: '#fff',
      }),
      // Body center of the 32px-tall pin (tip-anchored)
      offsetY: -20,
    }),
    font: '13px sans-serif',
  })
}

// One source of truth for marker/cluster colors (mirrors IconResolver precedence)
export const TRANSACTION_ICON_COLORS = {
  default: '#027BFA', // Blue
  ad_link: '#2DA74E', // Green
  ad_link_not_found: '#FFC107', // Amber
  not_vabaturu_tehing: '#FF4081', // Pink
  marked_for_comparison: '#DB3848', // Red
}

const svgDataUrl = svg => 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)

// Pre-encoded SVG icons cache for WebGL styles (shared across all layers)
let webglIconCache = null

/**
 * Initialize the WebGL icon cache with all icon types as data URLs.
 * Singles come in {house, land glyph} x {fresh, viewed} variants — viewed adds
 * the RED-236 corner badge at full colour (no desaturation). Clusters are
 * glyph-less and never carry viewed state. Called lazily on first use.
 */
const getWebGLIcons = () => {
  if (!webglIconCache) {
    webglIconCache = {}
    Object.entries(TRANSACTION_ICON_COLORS).forEach(([key, color]) => {
      webglIconCache[key] = svgDataUrl(createMarkerSvg(color))
      webglIconCache[`${key}_viewed`] = svgDataUrl(createMarkerSvg(color, null, 'house', true))
      webglIconCache[`${key}_land`] = svgDataUrl(createMarkerSvg(color, null, 'land'))
      webglIconCache[`${key}_land_viewed`] = svgDataUrl(createMarkerSvg(color, null, 'land', true))
      webglIconCache[key === 'default' ? 'cluster' : `cluster_${key}`] = svgDataUrl(createClusterSvg(color))
    })
  }
  return webglIconCache
}

/**
 * Create WebGL style array for all icon types.
 * In OpenLayers 10.x, the filter must be nested inside each style entry.
 * Returns an array of style objects, each with its own filter and nested style.
 *
 * Uses style variable `layerOpacity` for crossfade animations.
 * WebGL layers must be created with `variables: { layerOpacity: 1 }` option,
 * then use `layer.updateStyleVariables({ layerOpacity: value })` for animation.
 * Note: layer.setOpacity() does NOT work reliably with WebGLVectorLayer.
 *
 * @returns {Array} Style array for WebGLVectorLayer
 */
export const createWebGLStyleArray = () => {
  const icons = getWebGLIcons()

  // Style variables for layer-level animation control
  // Animated via layer.updateStyleVariables({ layerOpacity: value, layerScale: value })
  const layerOpacity = ['var', 'layerOpacity']

  // Note: properties are transformed from _icon/_land/... to icon/land/... to
  // avoid GLSL reserved-identifier issues; `land` and `viewed` are always
  // stamped 0/1 on every feature so the filters below never hit a missing attr.
  const iconKeys = Object.keys(TRANSACTION_ICON_COLORS)

  const iconMatch = key =>
    key === 'default'
      ? ['any', ['!', ['has', 'icon']], ['==', ['get', 'icon'], 'default'], ['==', ['get', 'icon'], '']]
      : ['==', ['get', 'icon'], key]

  const clusterIconMatch = key =>
    key === 'default'
      ? ['any', ['!', ['has', 'icon']], ['==', ['get', 'icon'], 'cluster'], ['==', ['get', 'icon'], '']]
      : ['==', ['get', 'icon'], `cluster_${key}`]

  const entries = []

  // Single markers: icon color x {house, land} glyph x {fresh, viewed}. RED-236
  // viewed markers keep FULL opacity — the corner badge is the only cue (no
  // visited-link dimming, no desaturation).
  iconKeys.forEach(key => {
    ;[false, true].forEach(land => {
      ;[false, true].forEach(viewed => {
        const suffix = `${land ? '_land' : ''}${viewed ? '_viewed' : ''}`
        entries.push({
          filter: [
            'all',
            ['!', ['get', 'cluster']],
            iconMatch(key),
            [land ? '==' : '!=', ['get', 'land'], 1],
            [viewed ? '==' : '!=', ['get', 'viewed'], 1],
          ],
          style: {
            'icon-src': icons[`${key}${suffix}`],
            'icon-scale': 1.0,
            'icon-anchor': [0.5, 1],
            'icon-opacity': layerOpacity,
          },
        })
      })
    })
  })

  // Cluster markers: color by dominant status, no glyph/viewed variants.
  // Counts render via the OL ≥ 10.10 WebGL text overlay. The overlay draws in
  // a worker, which imposes two constraints on these text props: no
  // @font-face inheritance (Quicksand falls back to system sans) and
  // semi-transparent stroke colors wash out white (premultiplied-alpha bug),
  // so the halo must be an opaque color — #444 @ 2.5 tuned by eye to match
  // getClusterTextStyle()'s rgba(0,0,0,0.25) look. No ['var', …] and no
  // declutter in text props. Hover redraws its own count on a canvas layer —
  // keep createHoverHighlightStyle()'s stroke in sync with this one.
  iconKeys.forEach(key => {
    entries.push({
      filter: ['all', ['get', 'cluster'], clusterIconMatch(key)],
      style: {
        'icon-src': icons[key === 'default' ? 'cluster' : `cluster_${key}`],
        'icon-scale': 1.0,
        'icon-anchor': [0.5, 1],
        'icon-opacity': layerOpacity,
        'text-value': ['get', 'count_label'],
        'text-font': 'bold 11px Quicksand, ui-sans-serif, system-ui, sans-serif',
        'text-fill-color': '#FFFFFF',
        'text-stroke-color': '#444444',
        'text-stroke-width': 2.5,
        'text-offset-y': -20,
      },
    })
  })

  return entries
}

// Hover-highlight styles (RED-255): the canvas layer that brings the hovered
// marker forward (WebGL has no per-feature z-order) and lights sibling parcels.
const hoverStyleCache = new Map()
const hoverParcelStyle = new Style({
  stroke: new Stroke({ color: '#027BFA', width: 3 }),
  fill: new Fill({ color: 'rgba(2, 123, 250, 0.12)' }),
})

/**
 * Style function for the transaction-hover-highlight layer. Features carry
 * either kind='parcel' (thick outline) or an `iconKey` into the shared WebGL
 * icon cache (enlarged marker clone, clusters with their count re-drawn so
 * the number is legible above everything).
 *
 * @returns {Function} Style function for VectorLayer
 */
export const createHoverHighlightStyle = () => feature => {
  if (feature.get('kind') === 'parcel') {
    return hoverParcelStyle
  }

  const iconKey = feature.get('iconKey') || 'default'
  const countLabel = feature.get('countLabel') || ''
  const cacheKey = `${iconKey}|${countLabel}`
  if (hoverStyleCache.has(cacheKey)) {
    return hoverStyleCache.get(cacheKey)
  }

  const icons = getWebGLIcons()
  const style = new Style({
    image: new Icon({
      src: icons[iconKey] || icons.default,
      scale: 1.18,
      anchor: [0.5, 1],
      crossOrigin: 'anonymous',
    }),
    text: countLabel
      ? new Text({
          text: countLabel,
          font: 'bold 13px Quicksand, ui-sans-serif, system-ui, sans-serif',
          fill: new Fill({ color: '#FFFFFF' }),
          // Mirrors the WebGL cluster-count halo (#444 @ 2.5); canvas strokes
          // read heavier than the worker-rendered WebGL ones, so 2 not 3
          stroke: new Stroke({ color: '#444444', width: 2 }),
          // Body center of the 32px-tall shape, scaled by the 1.18 zoom
          offsetY: -23,
        })
      : undefined,
  })

  if (hoverStyleCache.size > 200) {
    hoverStyleCache.delete(hoverStyleCache.keys().next().value)
  }
  hoverStyleCache.set(cacheKey, style)
  return style
}

// Cluster count text style cache (separate from label cache for different styling)
const clusterTextStyleCache = new Map()

/**
 * Get or create cached cluster count text style.
 * Uses white text centered on the cluster shape.
 *
 * @param {string} count - The count text to display (e.g., "42" or "99+")
 * @returns {Style} OpenLayers Style object
 */
const getClusterTextStyle = (count) => {
  if (clusterTextStyleCache.has(count)) {
    return clusterTextStyleCache.get(count)
  }

  const style = new Style({
    text: new Text({
      text: count,
      font: 'bold 11px Quicksand, ui-sans-serif, system-ui, sans-serif',
      fill: new Fill({ color: '#FFFFFF' }),
      // A count is essential info on its own icon — never declutter-dropped
      // (the layer declutters price labels); the dark halo keeps it legible
      // when crowded icons make it land on a neighbour.
      stroke: new Stroke({ color: 'rgba(0, 0, 0, 0.25)', width: 2 }),
      declutterMode: 'none',
      offsetY: -20, // Center on cluster shape body (shape is 32px tall with pointer at bottom)
    }),
  })

  // Limit cache size to prevent memory leaks
  if (clusterTextStyleCache.size > 300) {
    const firstKey = clusterTextStyleCache.keys().next().value
    clusterTextStyleCache.delete(firstKey)
  }

  clusterTextStyleCache.set(count, style)
  return style
}

/**
 * Create a style function that renders ONLY text labels (no icons).
 * Used as a hybrid overlay layer on top of WebGL layers since WebGL cannot render text.
 *
 * - For clusters: renders count number (1-99, or "99+" for larger clusters)
 * - For single features with labels: renders price/value label
 *
 * @returns {Function} Style function for VectorLayer
 */
export const createLabelOnlyStyle = () => {
  // Get or create cached text style (reuses global textStyleCache)
  const getTextStyle = (text, offsetY) => {
    const cacheKey = `${text}_${offsetY}`
    if (textStyleCache.has(cacheKey)) {
      return textStyleCache.get(cacheKey)
    }

    const textStyle = new Style({
      text: new Text({
        text: typeof text === 'number' ? text.toLocaleString('et-EE') : String(text),
        font: '12px sans-serif',
        backgroundFill: new Fill({
          color: 'rgba(255, 255, 255, 0.7)',
        }),
        backgroundStroke: new Stroke({
          color: '#6c757d',
        }),
        padding: [1, 1, 1, 2],
        offsetY: offsetY,
      }),
    })

    // Limit cache size to prevent memory leaks
    if (textStyleCache.size > 1000) {
      const firstKey = textStyleCache.keys().next().value
      textStyleCache.delete(firstKey)
    }

    textStyleCache.set(cacheKey, textStyle)
    return textStyle
  }

  // Return the style function
  return (feature, resolution) => {
    if (!feature) return null

    // Get properties - handle both OL features and GeoJSON features
    const props = feature.getProperties
      ? feature.getProperties()
      : feature.properties
    if (!props) return null

    // Cluster count labels — dead on the WebGL path since OL 10.10 (counts
    // render on the WebGLVectorLayer itself and clusters are no longer synced
    // here); kept while the spike settles, delete with getClusterTextStyle.
    if (props.cluster && props.point_count) {
      const count = props.point_count > 99 ? '99+' : String(props.point_count)
      return getClusterTextStyle(count)
    }

    // Get label value (support both property names)
    const label = props.label || props._label
    if (!label) return null

    // Determine state for offset calculation
    const isOpen = props.isOpen || props._open
    const isSelected = props.isSelected || props._selected
    const offsetY = isOpen ? -38 : (isSelected ? -43 : -34)

    return getTextStyle(label, offsetY)
  }
}
